import { useEffect, useMemo, useState } from "react";
import api, { formatMYR, formatDate } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, HandCoins, FileDown, Receipt } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { downloadMonthlyReportPDF } from "../lib/monthlyReport";

const emptyForm = {
    recipient: "",
    base_amount: 0,
    percent: 0,
    amount: 0,
    declare_date: new Date().toISOString().slice(0, 10),
    transfer_date: "",
    status: "pending",
    note: "",
    linked_sale_ids: [],
};

const now = new Date();
const YEARS = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

export default function PayoutsPage() {
    const [rows, setRows] = useState([]);
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [summary, setSummary] = useState({ total_revenue: 0, total_cost: 0, total_profit: 0 });

    const load = async () => {
        setLoading(true);
        const [{ data: list }, { data: s }, { data: allSales }] = await Promise.all([
            api.get("/payouts"),
            api.get("/dashboard/summary"),
            api.get("/sales"),
        ]);
        setRows(list);
        setSales(allSales);
        setSummary({ total_revenue: s.total_revenue, total_cost: s.total_cost || 0, total_profit: s.total_profit || 0 });
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    // Map sale.id -> sale
    const salesById = useMemo(() => Object.fromEntries(sales.map((s) => [s.id, s])), [sales]);

    // Set of sale ids already linked to any payout
    const linkedSaleIdsAll = useMemo(() => {
        const set = new Set();
        rows.forEach((r) => (r.linked_sale_ids || []).forEach((sid) => set.add(sid)));
        return set;
    }, [rows]);

    // Sum revenue of sales linked to any payout (dedup)
    const linkedTotal = useMemo(() => {
        let n = 0;
        linkedSaleIdsAll.forEach((sid) => {
            const s = salesById[sid];
            if (s) n += Number(s.total || 0);
        });
        return n;
    }, [linkedSaleIdsAll, salesById]);

    const unassignedTotal = Math.max(0, (summary.total_revenue || 0) - linkedTotal);

    const totals = useMemo(() => {
        const paid = rows.filter((r) => r.status === "paid").reduce((n, r) => n + r.amount, 0);
        const pending = rows.filter((r) => r.status !== "paid").reduce((n, r) => n + r.amount, 0);
        return { paid, pending, count_paid: rows.filter((r) => r.status === "paid").length, count_pending: rows.filter((r) => r.status !== "paid").length };
    }, [rows]);

    // 扣分成后 = 已分成账单额 - 已分成金额（实际支付出去）
    const netAfterPayouts = linkedTotal - totals.paid;

    // Month picker (Selects instead of native input to avoid clipping)
    const [reportYear, setReportYear] = useState(String(now.getFullYear()));
    const [reportMonthNum, setReportMonthNum] = useState(String(now.getMonth() + 1));
    const reportMonth = `${reportYear}-${reportMonthNum.padStart(2, "0")}`;
    const [reportBusy, setReportBusy] = useState(false);
    const [chartVisible, setChartVisible] = useState(true);

    useEffect(() => {
        api.get("/settings").then(({ data }) => setChartVisible(data.show_payout_chart !== false)).catch(() => {});
    }, []);

    const byRecipient = useMemo(() => {
        const map = {};
        rows.forEach((r) => {
            if (r.status !== "paid") return;
            map[r.recipient] = (map[r.recipient] || 0) + r.amount;
        });
        return Object.entries(map)
            .map(([name, value]) => ({ name, value: +value.toFixed(2) }))
            .sort((a, b) => b.value - a.value);
    }, [rows]);
    const PIE_COLORS = ["#B08A3E", "#2563EB", "#F59E0B", "#DC2626", "#7C3AED", "#0891B2", "#DB2777", "#65A30D"];

    const genReport = async () => {
        setReportBusy(true);
        try {
            const [y, m] = reportMonth.split("-").map(Number);
            const start = `${reportMonth}-01`;
            const endDate = new Date(y, m, 0).getDate();
            const end = `${reportMonth}-${String(endDate).padStart(2, "0")}`;
            const [{ data: s }, { data: monthSales }] = await Promise.all([
                api.get("/dashboard/summary", { params: { start_date: start, end_date: end } }),
                api.get("/sales", { params: { start_date: start, end_date: end } }),
            ]);
            const monthPayouts = rows.filter((r) => (r.declare_date || "").startsWith(reportMonth));
            void monthSales;
            await downloadMonthlyReportPDF({ month: reportMonth, summary: s, payouts: monthPayouts });
            toast.success(`已生成 ${reportMonth} 月度报表`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "生成失败");
        } finally {
            setReportBusy(false);
        }
    };

    const openCreate = () => {
        setEditing(null);
        setForm({
            ...emptyForm,
            base_amount: unassignedTotal || summary.total_revenue,
            declare_date: new Date().toISOString().slice(0, 10),
            linked_sale_ids: [],
        });
        setDialogOpen(true);
    };

    const openEdit = (r) => {
        setEditing(r);
        setForm({
            recipient: r.recipient,
            base_amount: r.base_amount,
            percent: r.percent,
            amount: r.amount,
            declare_date: r.declare_date || "",
            transfer_date: r.transfer_date || "",
            status: r.status,
            note: r.note || "",
            linked_sale_ids: [...(r.linked_sale_ids || [])],
        });
        setDialogOpen(true);
    };

    // Sales available in dialog = unassigned to any OTHER payout + already-linked to this payout
    const dialogSales = useMemo(() => {
        const currentLinked = new Set(form.linked_sale_ids || []);
        return sales
            .filter((s) => currentLinked.has(s.id) || !linkedSaleIdsAll.has(s.id))
            .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }, [sales, linkedSaleIdsAll, form.linked_sale_ids]);

    const dialogSelectedTotal = useMemo(() => {
        return (form.linked_sale_ids || []).reduce((n, sid) => {
            const s = salesById[sid];
            return n + (s ? Number(s.total || 0) : 0);
        }, 0);
    }, [form.linked_sale_ids, salesById]);

    const toggleSale = (sid, checked) => {
        const next = new Set(form.linked_sale_ids || []);
        if (checked) next.add(sid); else next.delete(sid);
        const arr = Array.from(next);
        // Auto refresh base_amount to match sum of selected sales
        const newBase = arr.reduce((n, s) => n + (salesById[s] ? Number(salesById[s].total) : 0), 0);
        setForm({ ...form, linked_sale_ids: arr, base_amount: +newBase.toFixed(2) });
    };

    const selectAllDialog = () => {
        const arr = dialogSales.map((s) => s.id);
        const newBase = arr.reduce((n, s) => n + Number(salesById[s]?.total || 0), 0);
        setForm({ ...form, linked_sale_ids: arr, base_amount: +newBase.toFixed(2) });
    };
    const clearAllDialog = () => setForm({ ...form, linked_sale_ids: [], base_amount: 0 });

    const previewAmount = useMemo(() => {
        const base = Number(form.base_amount || 0);
        const p = Number(form.percent || 0);
        return +(base * p / 100).toFixed(2);
    }, [form.base_amount, form.percent]);

    const useAutoAmount = () => setForm({ ...form, amount: previewAmount });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.recipient.trim()) return toast.error("请填写分成对象");
        setSaving(true);
        try {
            const payload = {
                ...form,
                base_amount: Number(form.base_amount || 0),
                percent: Number(form.percent || 0),
                amount: Number(form.amount || 0),
                linked_sale_ids: form.linked_sale_ids || [],
            };
            if (editing) {
                await api.put(`/payouts/${editing.id}`, payload);
                toast.success("分成记录已更新");
            } else {
                await api.post("/payouts", payload);
                toast.success("分成记录已创建");
            }
            setDialogOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "保存失败");
        } finally {
            setSaving(false);
        }
    };

    const markPaid = async (r) => {
        const today = new Date().toISOString().slice(0, 10);
        try {
            await api.put(`/payouts/${r.id}`, {
                ...r,
                transfer_date: r.transfer_date || today,
                status: "paid",
            });
            toast.success("已标记为已转账");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "操作失败");
        }
    };

    const del = async (r) => {
        if (!window.confirm(`确定删除分成给 ${r.recipient} 的记录？关联账单会释放回未分成。`)) return;
        await api.delete(`/payouts/${r.id}`);
        toast.success("已删除");
        load();
    };

    return (
        <div className="space-y-6" data-testid="payouts-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-gold-500">财务</div>
                    <h1 className="text-3xl font-bold tracking-tight text-slate-900 mt-1">分成记录</h1>
                    <p className="text-sm text-gold-500 mt-1">勾选要参与本次分成的销售单，剩下的显示在「未分成」，方便追踪账目</p>
                </div>
                <div className="flex items-center gap-2">
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="add-payout-btn" onClick={openCreate} className="bg-gold-600 hover:bg-gold-700 text-white gap-2">
                            <Plus className="h-4 w-4" /> 新增分成
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>{editing ? "编辑分成记录" : "新增分成记录"}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-4 pt-2">
                            <div className="space-y-1.5">
                                <Label>分成对象 *（合伙人 / 代销 / 销售员姓名）</Label>
                                <Input data-testid="payout-recipient-input" value={form.recipient} onChange={(e) => setForm({ ...form, recipient: e.target.value })} placeholder="如：合伙人 A" required />
                            </div>

                            {/* Sale picker */}
                            <div className="space-y-2 rounded-md border border-slate-200 p-3 bg-gold-50/40">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label className="inline-flex items-center gap-1.5"><Receipt className="h-3.5 w-3.5 text-gold-500" /> 选择要分成的账单（销售单）</Label>
                                        <div className="text-xs text-gold-500 mt-0.5">勾选后会自动计入基数。已被其他分成占用的账单不显示。</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button type="button" variant="ghost" size="sm" onClick={selectAllDialog} className="text-xs h-7">全选</Button>
                                        <Button type="button" variant="ghost" size="sm" onClick={clearAllDialog} className="text-xs h-7 text-gold-500">清空</Button>
                                    </div>
                                </div>
                                {dialogSales.length === 0 ? (
                                    <div className="text-xs text-gold-500 py-3 text-center">暂无可选账单</div>
                                ) : (
                                    <div className="max-h-56 overflow-y-auto border border-slate-200 rounded bg-white divide-y divide-slate-100">
                                        {dialogSales.map((s) => {
                                            const checked = form.linked_sale_ids?.includes(s.id);
                                            return (
                                                <label key={s.id} data-testid={`payout-sale-row-${s.id}`} className="flex items-center gap-3 px-3 py-2 hover:bg-gold-50 cursor-pointer">
                                                    <Checkbox
                                                        data-testid={`payout-sale-check-${s.id}`}
                                                        checked={checked}
                                                        onCheckedChange={(v) => toggleSale(s.id, !!v)}
                                                    />
                                                    <span className="text-xs font-mono font-semibold text-slate-900 w-32 shrink-0">{s.invoice_no}</span>
                                                    <span className="text-xs text-slate-600 flex-1 truncate">{s.customer_name || "散客"}</span>
                                                    <span className="text-xs text-gold-500 font-mono shrink-0">{formatDate(s.created_at).split(" ")[0]}</span>
                                                    <span className="text-sm font-mono font-bold text-gold-700 w-24 text-right shrink-0">{formatMYR(s.total)}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}
                                <div className="flex items-center justify-between text-xs pt-1">
                                    <span className="text-gold-500">已选 <span className="font-mono font-semibold text-slate-900">{form.linked_sale_ids?.length || 0}</span> 单</span>
                                    <span className="text-gold-500">合计 <span className="font-mono font-bold text-gold-700">{formatMYR(dialogSelectedTotal)}</span></span>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label>基数 (RM)</Label>
                                    <Input data-testid="payout-base-input" type="number" step="0.01" min="0" value={form.base_amount} onChange={(e) => setForm({ ...form, base_amount: e.target.value })} className="font-mono" />
                                    <div className="text-xs text-gold-500">勾选账单后自动填入合计</div>
                                </div>
                                <div className="space-y-1.5">
                                    <Label>百分比 (%)</Label>
                                    <Input data-testid="payout-percent-input" type="number" step="0.1" min="0" max="100" value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} className="font-mono" />
                                    <div className="text-xs text-gold-500">按基数自动算出金额</div>
                                </div>
                            </div>
                            <div className="rounded-md bg-gold-50 border border-gold-200 p-3 flex items-center justify-between">
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-gold-700 font-semibold">建议金额（基数 × %）</div>
                                    <div className="text-lg font-mono font-bold text-gold-800 mt-0.5">{formatMYR(previewAmount)}</div>
                                </div>
                                <Button type="button" variant="outline" size="sm" onClick={useAutoAmount} className="border-gold-300 text-gold-700">使用此金额</Button>
                            </div>
                            <div className="space-y-1.5">
                                <Label>实际分成金额 (RM) *</Label>
                                <Input data-testid="payout-amount-input" type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="font-mono" required />
                                <div className="text-xs text-gold-500">可手动调整（有的时候会四舍五入）</div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label>分账日期</Label>
                                    <Input data-testid="payout-declare-date" type="date" value={form.declare_date} onChange={(e) => setForm({ ...form, declare_date: e.target.value })} className="font-mono" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>转账日期</Label>
                                    <Input data-testid="payout-transfer-date" type="date" value={form.transfer_date} onChange={(e) => setForm({ ...form, transfer_date: e.target.value, status: e.target.value ? "paid" : form.status })} className="font-mono" />
                                    <div className="text-xs text-gold-500">留空 = 待转账</div>
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>备注</Label>
                                <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="例如：7 月合伙人分成" />
                            </div>
                            <DialogFooter className="pt-2">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>取消</Button>
                                <Button type="submit" data-testid="save-payout-btn" disabled={saving} className="bg-gold-600 hover:bg-gold-700 text-white">
                                    {saving ? "保存中..." : editing ? "保存修改" : "新增记录"}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card data-testid="payouts-unassigned-card" className="p-5 border-amber-200 bg-amber-50/40 shadow-sm">
                    <div className="text-xs font-semibold uppercase tracking-wider text-amber-800">未分成账单</div>
                    <div className="text-xl font-bold font-mono text-amber-700 mt-2">{formatMYR(unassignedTotal)}</div>
                    <div className="text-xs text-amber-700/80 mt-1">尚未参与任何分成的销售额</div>
                </Card>
                <Card className="p-5 border-slate-200 shadow-sm">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gold-500">已分成账单</div>
                    <div className="text-xl font-bold font-mono text-slate-900 mt-2">{formatMYR(linkedTotal)}</div>
                    <div className="text-xs text-gold-500 mt-1 font-mono">{linkedSaleIdsAll.size} 单</div>
                </Card>
                <Card data-testid="payouts-paid-card" className="p-5 border-slate-200 shadow-sm">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gold-500">已分成</div>
                    <div className="text-xl font-bold font-mono text-slate-900 mt-2">{formatMYR(totals.paid)}</div>
                    <div className="text-xs text-gold-500 mt-1 font-mono">{totals.count_paid} 笔</div>
                </Card>
                <Card className="p-5 border-slate-200 shadow-sm">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gold-500">扣分成后</div>
                    <div className={`text-xl font-bold font-mono mt-2 ${netAfterPayouts >= 0 ? "text-gold-700" : "text-red-600"}`}>{formatMYR(netAfterPayouts)}</div>
                    <div className="text-xs text-gold-500 mt-1">已分成账单 − 已分成</div>
                </Card>
            </div>

            {totals.count_pending > 0 && (
                <Card className="p-4 border-amber-200 bg-amber-50 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-amber-800">
                            <span className="font-semibold">待转账：</span>
                            <span className="font-mono">{formatMYR(totals.pending)}</span>
                            <span className="text-amber-700 ml-2">({totals.count_pending} 笔)</span>
                        </div>
                    </div>
                </Card>
            )}

            <Card className="p-4 border-slate-200 shadow-sm">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 shrink-0">
                        <FileDown className="h-4 w-4 text-gold-500" />
                        <span className="text-sm font-semibold text-slate-700">月度报表</span>
                    </div>
                    <Select value={reportYear} onValueChange={setReportYear}>
                        <SelectTrigger data-testid="report-year-select" className="h-9 w-[110px] font-mono">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {YEARS.map((y) => (
                                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={reportMonthNum} onValueChange={setReportMonthNum}>
                        <SelectTrigger data-testid="report-month-select" className="h-9 w-[110px] font-mono">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {MONTHS.map((m) => (
                                <SelectItem key={m} value={String(m)}>{m} 月</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button
                        data-testid="download-report-btn"
                        onClick={genReport}
                        disabled={reportBusy}
                        className="gap-2 h-9 bg-slate-900 hover:bg-slate-800 text-white"
                    >
                        <FileDown className="h-4 w-4" />
                        {reportBusy ? "生成中..." : "下载 PDF"}
                    </Button>
                    <span className="text-xs text-gold-500 ml-auto hidden sm:block">生成含销售 / 成本 / 净利润 / 分成的完整月度 PDF</span>
                </div>
            </Card>

            {byRecipient.length > 0 && chartVisible && (
                <Card data-testid="payout-pie-card" className="p-6 border-slate-200 shadow-sm">
                    <div className="flex items-baseline justify-between mb-4">
                        <div>
                            <h2 className="text-lg font-semibold tracking-tight text-slate-900">各对象已分金额</h2>
                            <div className="text-xs text-gold-500 mt-0.5">仅统计「已转账」的记录</div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
                        <div className="lg:col-span-2" style={{ width: "100%", height: 260 }}>
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie data={byRecipient} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={2} stroke="#ffffff" strokeWidth={2}>
                                        {byRecipient.map((r, i) => (
                                            <Cell key={r.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ backgroundColor: "#0F172A", border: "none", borderRadius: 6, color: "white" }}
                                        formatter={(v, n) => [formatMYR(v), n]}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="lg:col-span-3 space-y-2">
                            {byRecipient.map((r, i) => {
                                const pct = ((r.value / totals.paid) * 100).toFixed(1);
                                return (
                                    <div key={r.name} className="flex items-center gap-3">
                                        <span className="h-3 w-3 rounded-sm shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                                        <span className="text-sm font-medium text-slate-900 min-w-[100px]">{r.name}</span>
                                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                                        </div>
                                        <span className="text-sm font-mono font-semibold text-slate-900 w-24 text-right">{formatMYR(r.value)}</span>
                                        <span className="text-xs font-mono text-gold-500 w-12 text-right">{pct}%</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </Card>
            )}

            <Card className="border-slate-200 shadow-sm">
                {loading ? (
                    <div className="p-12 text-center text-gold-500">加载中...</div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center">
                        <HandCoins className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                        <div className="text-sm text-gold-500">还没有分成记录，点击右上角新增</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-slate-200 bg-gold-50/60">
                                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">对象</th>
                                    <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">账单数</th>
                                    <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">基数</th>
                                    <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">%</th>
                                    <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">金额</th>
                                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">分账日</th>
                                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">转账日</th>
                                    <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">状态</th>
                                    <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-gold-500">操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} data-testid={`payout-row-${r.id}`} className="border-b border-slate-100 hover:bg-gold-50 transition-colors">
                                        <td className="py-3 px-4">
                                            <div className="text-sm font-medium text-slate-900">{r.recipient}</div>
                                            {r.note && <div className="text-xs text-gold-500 mt-0.5 truncate max-w-[220px]">{r.note}</div>}
                                        </td>
                                        <td className="py-3 px-4 text-sm text-right font-mono text-slate-600">
                                            {(r.linked_sale_ids || []).length > 0 ? (
                                                <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 border-0 font-mono">{r.linked_sale_ids.length}</Badge>
                                            ) : <span className="text-slate-400">-</span>}
                                        </td>
                                        <td className="py-3 px-4 text-sm text-right font-mono text-slate-600">{formatMYR(r.base_amount)}</td>
                                        <td className="py-3 px-4 text-sm text-right font-mono text-slate-600">{r.percent ? `${r.percent}%` : "-"}</td>
                                        <td className="py-3 px-4 text-sm text-right font-mono font-bold text-slate-900">{formatMYR(r.amount)}</td>
                                        <td className="py-3 px-4 text-sm text-slate-600 font-mono">{r.declare_date || "-"}</td>
                                        <td className="py-3 px-4 text-sm text-slate-600 font-mono">{r.transfer_date || <span className="text-slate-400 italic">待转账</span>}</td>
                                        <td className="py-3 px-4 text-center">
                                            {r.status === "paid" ? (
                                                <Badge className="bg-gold-100 text-gold-700 hover:bg-gold-100 border-0">已转账</Badge>
                                            ) : (
                                                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-0">待转账</Badge>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                {r.status !== "paid" && (
                                                    <Button data-testid={`mark-paid-${r.id}`} variant="ghost" size="sm" onClick={() => markPaid(r)} className="text-gold-700 hover:bg-gold-50 gap-1.5" title="标记为已转账">
                                                        <Check className="h-3.5 w-3.5" />
                                                        <span className="hidden lg:inline text-xs">已转</span>
                                                    </Button>
                                                )}
                                                <Button variant="ghost" size="sm" onClick={() => openEdit(r)} className="text-slate-600">
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => del(r)} className="text-red-600 hover:bg-red-50">
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}
